/* The one sum the client computes on money (S-18a, owner decision).
 *
 * `frontend/README.md` says money arrives as a decimal string and is never
 * computed here. The carrier payment sheet is the recorded exception: its
 * «Итого к оплате» is the sum of the amounts typed on the rows the reader
 * ticked, and it IS the payment amount the request carries — there is no
 * served figure to print, because nothing has been sent yet. So the sum is
 * done here, exactly, on the strings:
 *
 *   · never through `Number()`: a float holds 0.1 + 0.2 as 0.30000000000000004
 *     and cannot hold a cent past 2^53 at all;
 *   · on scaled `BigInt`s, which are integers of any size;
 *   · refusing anything that is not a plain non-negative decimal, so a blank or
 *     half-typed row shows no total rather than a wrong one.
 *
 * The server's 422 `items: sum-mismatch` is the backstop: if this ever
 * disagreed with the items it is asked to record, nothing is written.
 *
 * The SECOND recorded exception (S-21, owner) is the client payment sheet's
 * footer: «Разнесено» is the sum of the amounts typed on its rows and «Не
 * разнесено» the received amount less that sum (`decimalDifference`, signed —
 * a row list that spends more than the payment reads below zero); its blocking
 * comparisons (a row above what its document still takes, the rows above the
 * payment) go through `compareDecimal`. There, too, the server is the
 * backstop: both ceilings are judged again under row locks.
 */

const PLAIN = /^(\d+)(?:\.(\d+))?$/

interface Scaled {
  digits: bigint
  scale: number
}

function parse(value: string): Scaled | null {
  const match = PLAIN.exec(value)
  if (!match) return null
  const [, whole = '0', fraction = ''] = match
  return { digits: BigInt(whole + fraction), scale: fraction.length }
}

function rescale(value: Scaled, scale: number): bigint {
  return value.digits * 10n ** BigInt(scale - value.scale)
}

function render(digits: bigint, scale: number): string {
  if (digits < 0n) return `-${render(-digits, scale)}`
  if (scale === 0) return digits.toString()
  const text = digits.toString().padStart(scale + 1, '0')
  return `${text.slice(0, -scale)}.${text.slice(-scale)}`
}

/** The exact sum of plain decimal strings (`1234.56`), at the widest scale
 *  among them — `['1', '2.50']` is `'3.50'`. `'0'` for no values; `null` when
 *  any value is not a plain non-negative decimal (normalise first). */
export function decimalSum(values: readonly string[]): string | null {
  const parsed: Scaled[] = []
  for (const value of values) {
    const scaled = parse(value)
    if (scaled === null) return null
    parsed.push(scaled)
  }
  const scale = parsed.reduce((widest, value) => Math.max(widest, value.scale), 0)
  const total = parsed.reduce((sum, value) => sum + rescale(value, scale), 0n)
  return render(total, scale)
}

/** -1, 0 or 1 as `a` is below, equal to or above `b` by VALUE (`'3000'`
 *  equals `'3000.00'`); `null` when either is not a plain non-negative
 *  decimal. For a rule that compares two figures in one currency — never for
 *  arithmetic. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 | null {
  const left = parse(a)
  const right = parse(b)
  if (left === null || right === null) return null
  const scale = Math.max(left.scale, right.scale)
  const x = rescale(left, scale)
  const y = rescale(right, scale)
  return x < y ? -1 : x > y ? 1 : 0
}

/** `a − b`, exactly, at the widest scale of the two — SIGNED, so `'2455.25'`
 *  less `'2700.00'` is `'-244.75'`; `null` when either is not a plain
 *  non-negative decimal (a blank or half-typed field shows no figure rather
 *  than a wrong one). */
export function decimalDifference(a: string, b: string): string | null {
  const left = parse(a)
  const right = parse(b)
  if (left === null || right === null) return null
  const scale = Math.max(left.scale, right.scale)
  return render(rescale(left, scale) - rescale(right, scale), scale)
}
